/**
 * useHomepagePolling Hook
 *
 * Centralized polling manager for homepage data fetching.
 * Handles multiple request types (active orders, notifications, etc.)
 * with configurable intervals and callbacks.
 *
 * Features:
 * - Scalable architecture for adding new polling requests
 * - Individual control over polling intervals
 * - Automatic cleanup on unmount
 * - Error handling for each request type
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import useRequest from './useRequest';
import { useAuth } from 'context/AuthContext';
import { useOrder } from 'context/OrderContext';
import { USER_ROLES, ORDER_STATUS } from 'utils/constants';
import { getStatusMapping } from './useActiveOrderChecker';

/**
 * Request types that can be polled
 */
const POLLING_TYPES = {
    ACTIVE_ORDERS: 'activeOrders',
    NOTIFICATIONS: 'notifications',
    // Add more types here as needed
    // MESSAGES: 'messages',
    // ALERTS: 'alerts',
};

/**
 * Default polling intervals (in milliseconds)
 */
const DEFAULT_INTERVALS = {
    [POLLING_TYPES.ACTIVE_ORDERS]: 30000,  // 30 seconds
    [POLLING_TYPES.NOTIFICATIONS]: 45000,   // 45 seconds
};

/**
 * Hook configuration options
 * @typedef {Object} PollingConfig
 * @property {boolean} enabled - Whether to enable polling for this type
 * @property {number} interval - Polling interval in milliseconds
 * @property {function} onSuccess - Callback when request succeeds
 * @property {function} onError - Callback when request fails
 * @property {boolean} immediate - Whether to fetch immediately on mount
 */

/**
 * Main hook for managing homepage polling
 *
 * @param {Object} config - Configuration object
 * @param {PollingConfig} config.activeOrders - Config for active orders polling
 * @param {PollingConfig} config.notifications - Config for notifications polling
 * @param {boolean} config.enabled - Global enable/disable switch
 * @returns {Object} Hook state and methods
 */
const useHomepagePolling = (config = {}) => {
    const { sendRequest } = useRequest();
    const { user } = useAuth();
    const { startMatchingProcess, resetOrderContext } = useOrder();

    // State for tracking latest data
    const [data, setData] = useState({
        [POLLING_TYPES.ACTIVE_ORDERS]: null,
        [POLLING_TYPES.NOTIFICATIONS]: null,
    });

    // State for tracking loading states
    const [loading, setLoading] = useState({
        [POLLING_TYPES.ACTIVE_ORDERS]: false,
        [POLLING_TYPES.NOTIFICATIONS]: false,
    });

    // State for tracking errors
    const [errors, setErrors] = useState({
        [POLLING_TYPES.ACTIVE_ORDERS]: null,
        [POLLING_TYPES.NOTIFICATIONS]: null,
    });

    // Track if there are unread notifications
    const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);

    // Refs to store interval IDs
    const intervals = useRef({});

    // Merge default config with user config
    const mergedConfig = {
        enabled: config.enabled !== false,
        activeOrders: {
            enabled: true,
            interval: DEFAULT_INTERVALS[POLLING_TYPES.ACTIVE_ORDERS],
            immediate: true,
            ...config.activeOrders,
        },
        notifications: {
            enabled: true,
            interval: DEFAULT_INTERVALS[POLLING_TYPES.NOTIFICATIONS],
            immediate: true,
            ...config.notifications,
        },
    };

    /**
     * Fetch active orders
     */
    const fetchActiveOrders = useCallback(async () => {
        if (!user || !mergedConfig.activeOrders.enabled) return;

        setLoading(prev => ({ ...prev, [POLLING_TYPES.ACTIVE_ORDERS]: true }));
        setErrors(prev => ({ ...prev, [POLLING_TYPES.ACTIVE_ORDERS]: null }));

        try {
            const response = await sendRequest({
                method: 'GET',
                url: '/module/order/checkForActiveOrders',
            });

            if (response?.status === 'success') {
                const orderData = response.data;
                setData(prev => ({ ...prev, [POLLING_TYPES.ACTIVE_ORDERS]: orderData }));

                // Handle order context updates based on user role
                if (orderData && shouldProcessOrder(user.userRole, orderData.deliveryStatus)) {
                    // Calculate the correct UI step based on user role and order type
                    const orderType = orderData.type || 0; // Default to Bringzz Pickup if type is missing
                    const statusMapping = getStatusMapping(user.userRole, orderType);

                    // Convert deliveryStatus to string for lookup (JS object keys are strings)
                    const statusKey = String(orderData.deliveryStatus);
                    const uiStep = statusMapping[statusKey] !== undefined ? statusMapping[statusKey] : 0;

                    console.log('useHomepagePolling: Calculated step', {
                        userRole: user.userRole,
                        orderType: orderType,
                        deliveryStatus: orderData.deliveryStatus,
                        statusKey: statusKey,
                        uiStep: uiStep,
                        mappingKeys: Object.keys(statusMapping).slice(0, 5)
                    });

                    startMatchingProcess(orderData, uiStep);

                    if (mergedConfig.activeOrders.onSuccess) {
                        mergedConfig.activeOrders.onSuccess(orderData);
                    }
                } else {
                    resetOrderContext();
                    if (mergedConfig.activeOrders.onSuccess) {
                        mergedConfig.activeOrders.onSuccess(null);
                    }
                }
            } else {
                // No active orders
                setData(prev => ({ ...prev, [POLLING_TYPES.ACTIVE_ORDERS]: null }));
                resetOrderContext();

                if (mergedConfig.activeOrders.onSuccess) {
                    mergedConfig.activeOrders.onSuccess(null);
                }
            }
        } catch (error) {
            console.error('Error fetching active orders:', error);
            setErrors(prev => ({ ...prev, [POLLING_TYPES.ACTIVE_ORDERS]: error }));

            if (mergedConfig.activeOrders.onError) {
                mergedConfig.activeOrders.onError(error);
            }
        } finally {
            setLoading(prev => ({ ...prev, [POLLING_TYPES.ACTIVE_ORDERS]: false }));
        }
    }, [user, sendRequest, startMatchingProcess, resetOrderContext]);

    /**
     * Fetch notifications
     */
    const fetchNotifications = useCallback(async () => {
        if (!user || !mergedConfig.notifications.enabled) return;

        setLoading(prev => ({ ...prev, [POLLING_TYPES.NOTIFICATIONS]: true }));
        setErrors(prev => ({ ...prev, [POLLING_TYPES.NOTIFICATIONS]: null }));

        try {
            const response = await sendRequest({
                method: 'GET',
                url: '/module/notification/fetchNotificationsByUser',
            });

            if (response?.status === 'success') {
                const notifications = response.data || [];
                setData(prev => ({ ...prev, [POLLING_TYPES.NOTIFICATIONS]: notifications }));

                // Check if there are unread notifications
                const unreadCount = notifications.filter(n => n.read === 0).length;
                setHasUnreadNotifications(unreadCount > 0);

                if (mergedConfig.notifications.onSuccess) {
                    mergedConfig.notifications.onSuccess({
                        notifications,
                        unreadCount,
                        hasUnread: unreadCount > 0
                    });
                }
            } else if (response?.status === 'error' && response?.message === 'No notifications found') {
                // No notifications
                setData(prev => ({ ...prev, [POLLING_TYPES.NOTIFICATIONS]: [] }));
                setHasUnreadNotifications(false);

                if (mergedConfig.notifications.onSuccess) {
                    mergedConfig.notifications.onSuccess({
                        notifications: [],
                        unreadCount: 0,
                        hasUnread: false
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
            setErrors(prev => ({ ...prev, [POLLING_TYPES.NOTIFICATIONS]: error }));

            if (mergedConfig.notifications.onError) {
                mergedConfig.notifications.onError(error);
            }
        } finally {
            setLoading(prev => ({ ...prev, [POLLING_TYPES.NOTIFICATIONS]: false }));
        }
    }, [user, sendRequest]);

    /**
     * Helper to determine if order should be processed
     */
    const shouldProcessOrder = (userRole, deliveryStatus) => {
        switch (userRole) {
            case USER_ROLES.PHARMACY_ROLE:
                return deliveryStatus < ORDER_STATUS.ORDER_CONFIRMED_BY_SHOPPER;
            case USER_ROLES.SHOPPER_ROLE:
                return deliveryStatus < ORDER_STATUS.ORDER_CONFIRMED_BY_SHOPPER_WITH_PACKAGE;
            case USER_ROLES.CONSUMER_ROLE:
                return deliveryStatus < ORDER_STATUS.DONE;
            default:
                return false;
        }
    };

    /**
     * Start polling for a specific type
     */
    const startPolling = useCallback((type, fetchFunction, interval) => {
        // Clear existing interval if any
        if (intervals.current[type]) {
            clearInterval(intervals.current[type]);
        }

        // Start new interval
        intervals.current[type] = setInterval(fetchFunction, interval);
    }, []);

    /**
     * Stop polling for a specific type
     */
    const stopPolling = useCallback((type) => {
        if (intervals.current[type]) {
            clearInterval(intervals.current[type]);
            intervals.current[type] = null;
        }
    }, []);

    /**
     * Stop all polling
     */
    const stopAllPolling = useCallback(() => {
        Object.keys(intervals.current).forEach(type => {
            stopPolling(type);
        });
    }, [stopPolling]);

    /**
     * Manual refresh of specific data type
     */
    const refresh = useCallback(async (type) => {
        switch (type) {
            case POLLING_TYPES.ACTIVE_ORDERS:
                await fetchActiveOrders();
                break;
            case POLLING_TYPES.NOTIFICATIONS:
                await fetchNotifications();
                break;
            default:
                console.warn(`Unknown polling type: ${type}`);
        }
    }, [fetchActiveOrders, fetchNotifications]);

    /**
     * Manual refresh of all data
     */
    const refreshAll = useCallback(async () => {
        const promises = [];

        if (mergedConfig.activeOrders.enabled) {
            promises.push(fetchActiveOrders());
        }

        if (mergedConfig.notifications.enabled) {
            promises.push(fetchNotifications());
        }

        await Promise.all(promises);
    }, [fetchActiveOrders, fetchNotifications]);

    // Setup initial fetch and polling on mount
    useEffect(() => {
        if (!user || !mergedConfig.enabled) return;

        // Initial fetch for enabled types
        if (mergedConfig.activeOrders.enabled && mergedConfig.activeOrders.immediate) {
            fetchActiveOrders();
        }

        if (mergedConfig.notifications.enabled && mergedConfig.notifications.immediate) {
            fetchNotifications();
        }

        // Setup polling intervals
        if (mergedConfig.activeOrders.enabled && mergedConfig.activeOrders.interval > 0) {
            startPolling(
                POLLING_TYPES.ACTIVE_ORDERS,
                fetchActiveOrders,
                mergedConfig.activeOrders.interval
            );
        }

        if (mergedConfig.notifications.enabled && mergedConfig.notifications.interval > 0) {
            startPolling(
                POLLING_TYPES.NOTIFICATIONS,
                fetchNotifications,
                mergedConfig.notifications.interval
            );
        }

        // Cleanup on unmount
        return () => {
            stopAllPolling();
        };
    }, [user, mergedConfig.enabled]);

    return {
        // Data
        data,
        activeOrder: data[POLLING_TYPES.ACTIVE_ORDERS],
        notifications: data[POLLING_TYPES.NOTIFICATIONS],
        hasUnreadNotifications,

        // Loading states
        loading,
        isLoadingOrders: loading[POLLING_TYPES.ACTIVE_ORDERS],
        isLoadingNotifications: loading[POLLING_TYPES.NOTIFICATIONS],

        // Errors
        errors,

        // Methods
        refresh,
        refreshAll,
        stopPolling,
        stopAllPolling,

        // Manual fetch methods
        fetchActiveOrders,
        fetchNotifications,
    };
};

export default useHomepagePolling;
export { POLLING_TYPES, DEFAULT_INTERVALS };